import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ChangeEventHandler } from 'react';

import { ConfirmModal } from './ConfirmModal.tsx';
import { CustomerPicker, type CustomerPickerValue } from './CustomerPicker.tsx';
import { DownloadPdfButton } from './DownloadPdfButton.tsx';
import { InvoiceItemsTable, type InvoiceEditorItem } from './InvoiceItemsTable.tsx';
import { InvoiceTotals } from './InvoiceTotals.tsx';
import { NativeSelect } from './NativeSelect.tsx';
import { StatusMessage } from './StatusMessage.tsx';
import { TextArea } from './TextArea.tsx';
import { TextField } from './TextField.tsx';
import { WorkspaceContainer } from './WorkspaceLayout.tsx';
import {
  ORIGINAL_PDF_DOWNLOAD_ERROR,
  downloadOriginalInvoicePdf,
  useUpdateInvoicePaymentStatus,
} from '../lib/invoices.ts';
import type { DownloadInvoicePdfInput, DownloadInvoicePdfResult } from '../pdf/download.ts';
import {
  invoiceDetailToPdfModel,
  invoiceDraftToPdfModel,
  type PdfInvoiceDraftInput,
  type PdfInvoiceModel,
  type PdfParty,
} from '../pdf/model.ts';
import { validatePdfOnePageLayout } from '../pdf/preflight.ts';
import { loadPdfSignature, type PdfSignature } from '../pdf/signature.ts';
import {
  INVALID_INVOICE_INPUT_ERROR,
  INVOICE_DELETE_ERROR,
  INVOICE_PAYMENT_STATUS_ERROR,
  INVOICE_SAVE_ERROR,
  UpsertInvoiceInputSchema,
  type Currency,
  type InvoiceDetail,
  type InvoiceDetailResponse,
  type InvoicePartyInput,
  type InvoiceSnapshot,
  type UpsertInvoiceInput,
} from '../../shared/invoice.ts';
import type { Counterparty } from '../../shared/counterparty.ts';
import type { SettingsResponse } from '../../shared/settings.ts';
import { todayLocalIsoDate } from '../../shared/format.ts';

type SaveMode = 'stay' | 'close';

interface PartyState {
  intent: InvoicePartyInput['type'];
  customerId?: string;
  snapshot?: InvoiceSnapshot | null;
}

interface InvoiceFormState {
  date: string;
  dueDate: string;
  currency: Currency;
  billTo: PartyState;
  shipTo: PartyState;
  notes: string;
  items: InvoiceEditorItem[];
  taxRate: string;
  taxLabel: string;
}

export interface InvoiceEditorProps {
  mode: 'create' | 'edit';
  settings: SettingsResponse;
  customers: Counterparty[];
  invoice?: InvoiceDetail;
  initialSaveMessage?: string | null;
  onSave: (input: UpsertInvoiceInput) => Promise<InvoiceDetailResponse>;
  onSaveAndClose: (input: UpsertInvoiceInput) => Promise<InvoiceDetailResponse>;
  onDelete?: () => Promise<void>;
}

export interface InvoiceEditorPdfDependencies {
  download?: (input: DownloadInvoicePdfInput) => Promise<DownloadInvoicePdfResult>;
  loadSignature?: (signaturePreviewUrl: string | null | undefined) => Promise<PdfSignature | null>;
  downloadOriginal?: (invoiceId: string) => Promise<void>;
}

type EditorPdfCandidate =
  | { kind: 'ready'; model: PdfInvoiceModel }
  | { kind: 'invalid'; message: string }
  | { kind: 'overflow'; message: string };

interface InvoiceDocumentSurfaceProps {
  invoiceNumber: number;
  senderName: string;
  senderSignatureUrl: string | null;
  form: InvoiceFormState;
  readOnly: boolean;
  setForm: React.Dispatch<React.SetStateAction<InvoiceFormState>>;
  setCurrency: (currency: Currency) => void;
  setNotesPristine: React.Dispatch<React.SetStateAction<boolean>>;
  selectedParties: { billTo: CustomerPickerValue | null; shipTo: CustomerPickerValue | null };
  updateParty: (kind: 'billTo' | 'shipTo', customer: Counterparty | null) => void;
}

interface InvoiceEditorActionState {
  readOnly: boolean;
  busy: boolean;
  savingMode: SaveMode | null;
  save: (modeToSave: SaveMode) => Promise<void>;
  downloadInvoice: PdfInvoiceModel | null;
  signature: PdfSignature | null;
  signatureLoading: boolean;
  download?: (input: DownloadInvoicePdfInput) => Promise<DownloadInvoicePdfResult>;
  archiveInvoiceId: string | null;
  downloadOriginal?: (invoiceId: string) => Promise<void>;
  paymentToggle: PaymentToggleState | null;
  deleteAction: DeleteActionState | null;
}

interface DeleteActionState {
  invoiceNumber: number;
  pending: boolean;
  requestDelete: () => void;
}

interface PaymentToggleState {
  isPaid: boolean;
  pending: boolean;
  toggle: () => void;
}

interface InvoiceEditorActionsProps extends InvoiceEditorActionState {
  label: string;
  className: string;
}

interface InvoiceEditorLayoutProps {
  title: string;
  archiveInvoice: boolean;
  paymentStatus: 'Paid' | 'Unpaid' | null;
  saveMessage: string | null;
  saveError: string | null;
  paymentError: string | null;
  deleteError: string | null;
  pdfIssue: string | null;
  document: InvoiceDocumentSurfaceProps;
  actions: InvoiceEditorActionState;
}

let invoiceEditorPdfDependencies: InvoiceEditorPdfDependencies = {};

// eslint-disable-next-line react-refresh/only-export-components -- test-only route dependency injection.
export function setInvoiceEditorPdfDependenciesForTests(
  dependencies: InvoiceEditorPdfDependencies,
): void {
  invoiceEditorPdfDependencies = dependencies;
}

// eslint-disable-next-line react-refresh/only-export-components -- test-only route dependency injection.
export function resetInvoiceEditorPdfDependenciesForTests(): void {
  invoiceEditorPdfDependencies = {};
}

export function InvoiceEditor({
  mode,
  settings,
  customers,
  invoice,
  initialSaveMessage = null,
  onSave,
  onSaveAndClose,
  onDelete,
}: InvoiceEditorProps): React.ReactElement {
  const readOnly = invoice?.isArchive === true;
  const [form, setForm] = useState<InvoiceFormState>(() => toInitialForm(settings, invoice));
  const [notesPristine, setNotesPristine] = useState(invoice ? false : true);
  const [saveMessage, setSaveMessage] = useState<string | null>(initialSaveMessage);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingMode, setSavingMode] = useState<SaveMode | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const updatePaymentStatus = useUpdateInvoicePaymentStatus();
  const invoiceNumber = invoice?.number ?? settings.settings.nextInvoiceNumber;
  const title = mode === 'create' ? 'New invoice' : `Invoice #${invoiceNumber}`;
  const senderName = invoice?.senderProfile.name ?? settings.senderProfile.name;
  const senderSignatureUrl = invoice
    ? invoice.senderProfile.signaturePreviewUrl
    : settings.senderProfile.signaturePreviewUrl;
  const paymentStatus = mode === 'edit' && invoice ? (invoice.isPaid ? 'Paid' : 'Unpaid') : null;
  const busy = savingMode !== null;
  const pdfDependencies = invoiceEditorPdfDependencies;

  const partySnapshots = useMemo(
    () => ({
      billTo: resolvePartySnapshot(form.billTo, customers),
      shipTo: resolvePartySnapshot(form.shipTo, customers),
    }),
    [customers, form.billTo, form.shipTo],
  );
  const selectedParties = useMemo(
    () => ({
      billTo: resolveSelectedCustomer(form.billTo, customers),
      shipTo: resolveSelectedCustomer(form.shipTo, customers),
    }),
    [customers, form.billTo, form.shipTo],
  );
  const currentPdfCandidate = useMemo(
    () =>
      buildEditorPdfCandidate({
        form,
        invoice,
        invoiceNumber,
        partySnapshots,
        settings,
      }),
    [form, invoice, invoiceNumber, partySnapshots, settings],
  );
  const signatureUrl =
    currentPdfCandidate.kind === 'ready'
      ? currentPdfCandidate.model.sender.signaturePreviewUrl
      : null;
  const { signature, loading: signatureLoading } = usePdfSignature(
    signatureUrl,
    pdfDependencies.loadSignature,
  );
  const downloadInvoice =
    invoice && currentPdfCandidate.kind === 'ready' ? currentPdfCandidate.model : null;
  const pdfIssue = currentPdfCandidate.kind === 'ready' ? null : currentPdfCandidate.message;

  useEffect(() => {
    if (!saveMessage) return undefined;

    const message = saveMessage;
    const timeout = window.setTimeout(() => {
      setSaveMessage((current) => (current === message ? null : current));
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [saveMessage]);

  function setCurrency(currency: Currency): void {
    setForm((current) => ({
      ...current,
      currency,
      notes: notesPristine ? notesTemplateFor(settings, currency) : current.notes,
    }));
  }

  function updateParty(kind: 'billTo' | 'shipTo', customer: Counterparty | null): void {
    setForm((current) => {
      const nextParty: PartyState = customer
        ? {
            intent: 'selectCustomer',
            customerId: customer.id,
            snapshot: snapshotFromCustomer(customer),
          }
        : { intent: invoice ? 'clear' : 'none', snapshot: null };
      return { ...current, [kind]: nextParty };
    });
  }

  async function togglePaymentStatus(): Promise<void> {
    if (!invoice || updatePaymentStatus.isPending) return;
    setSaveMessage(null);
    setSaveError(null);
    setPaymentError(null);
    try {
      const response = await updatePaymentStatus.mutateAsync({
        id: invoice.id,
        isPaid: !invoice.isPaid,
      });
      setSaveMessage(response.invoice.isPaid ? 'Marked as paid.' : 'Marked as unpaid.');
    } catch {
      setPaymentError(INVOICE_PAYMENT_STATUS_ERROR);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!invoice || !onDelete || deletePending) return;
    setSaveMessage(null);
    setSaveError(null);
    setPaymentError(null);
    setDeleteError(null);
    setDeletePending(true);
    try {
      await onDelete();
      setDeleteConfirmOpen(false);
    } catch {
      setDeleteConfirmOpen(false);
      setDeleteError(INVOICE_DELETE_ERROR);
    } finally {
      setDeletePending(false);
    }
  }

  const canDelete = !readOnly && invoice !== undefined && onDelete !== undefined;
  const deleteAction: DeleteActionState | null = canDelete
    ? {
        invoiceNumber: invoice.number,
        pending: deletePending,
        requestDelete: () => {
          setDeleteError(null);
          setDeleteConfirmOpen(true);
        },
      }
    : null;

  async function save(modeToSave: SaveMode): Promise<void> {
    setSaveMessage(null);
    setSaveError(null);
    let input: UpsertInvoiceInput;
    try {
      input = UpsertInvoiceInputSchema.parse(toUpsertInput(form));
    } catch {
      setSaveError(INVALID_INVOICE_INPUT_ERROR);
      return;
    }

    setSavingMode(modeToSave);
    try {
      const response = modeToSave === 'close' ? await onSaveAndClose(input) : await onSave(input);
      setSaveMessage(`Invoice #${response.invoice.number} saved.`);
    } catch {
      setSaveError(INVOICE_SAVE_ERROR);
    } finally {
      setSavingMode(null);
    }
  }

  return (
    <>
      <InvoiceEditorLayout
        title={title}
        archiveInvoice={readOnly}
        paymentStatus={paymentStatus}
        saveMessage={saveMessage}
        saveError={saveError}
        paymentError={paymentError}
        deleteError={deleteError}
        pdfIssue={pdfIssue}
        document={{
          invoiceNumber,
          senderName,
          senderSignatureUrl,
          form,
          readOnly,
          setForm,
          setCurrency,
          setNotesPristine,
          selectedParties,
          updateParty,
        }}
        actions={{
          readOnly,
          busy,
          savingMode,
          save,
          downloadInvoice,
          signature,
          signatureLoading,
          download: pdfDependencies.download,
          archiveInvoiceId: invoice?.isArchive ? invoice.id : null,
          downloadOriginal: pdfDependencies.downloadOriginal,
          paymentToggle: invoice
            ? {
                isPaid: invoice.isPaid,
                pending: updatePaymentStatus.isPending,
                toggle: () => {
                  void togglePaymentStatus();
                },
              }
            : null,
          deleteAction,
        }}
      />
      {deleteAction ? (
        <ConfirmModal
          open={deleteConfirmOpen}
          title="Delete invoice"
          confirmLabel="Delete invoice"
          confirming={deletePending}
          onCancel={() => {
            if (deletePending) return;
            setDeleteConfirmOpen(false);
          }}
          onConfirm={() => {
            void confirmDelete();
          }}
        >
          <p>
            Delete <span className="font-medium">Invoice #{deleteAction.invoiceNumber}</span>? This
            cannot be undone.
          </p>
        </ConfirmModal>
      ) : null}
    </>
  );
}

function InvoiceEditorLayout({
  title,
  archiveInvoice,
  paymentStatus,
  saveMessage,
  saveError,
  paymentError,
  deleteError,
  pdfIssue,
  document,
  actions,
}: InvoiceEditorLayoutProps): React.ReactElement {
  return (
    <div className="min-h-screen overflow-x-hidden">
      <WorkspaceContainer as="main" className="overflow-x-hidden py-6 sm:py-8">
        <InvoiceEditorHeader title={title} />
        <InvoiceEditorStatus
          archiveInvoice={archiveInvoice}
          paymentStatus={paymentStatus}
          saveMessage={saveMessage}
          saveError={saveError}
          paymentError={paymentError}
          deleteError={deleteError}
          pdfIssue={pdfIssue}
          className="mt-4"
        />
        <InvoiceEditorGrid document={document} actions={actions} />
      </WorkspaceContainer>
    </div>
  );
}

function InvoiceEditorGrid({
  document,
  actions,
}: {
  document: InvoiceDocumentSurfaceProps;
  actions: InvoiceEditorActionState;
}): React.ReactElement {
  return (
    <div className="mt-4 grid min-w-0 gap-6 xl:grid-cols-[minmax(0,72rem)_16rem] xl:items-start">
      <div className="min-w-0 space-y-4">
        <InvoiceDocumentSurface {...document} />
        <InvoiceEditorActions
          label="Mobile invoice actions"
          className="rounded-2xl border border-line bg-paper-soft p-5 shadow-[0_10px_28px_-26px_rgba(31,27,21,0.45)] xl:hidden"
          {...actions}
        />
      </div>

      <aside className="hidden space-y-4 xl:sticky xl:top-24 xl:block">
        <InvoiceEditorActions
          label="Desktop invoice actions"
          className="rounded-2xl border border-line bg-paper-soft p-5 shadow-[0_10px_28px_-26px_rgba(31,27,21,0.45)]"
          {...actions}
        />
      </aside>
    </div>
  );
}

function InvoiceDocumentSurface(props: InvoiceDocumentSurfaceProps): React.ReactElement {
  return (
    <section
      aria-label="Invoice document"
      className="min-w-0 max-w-full overflow-x-hidden rounded-2xl border border-line bg-paper-soft p-6 shadow-[0_18px_44px_-32px_rgba(31,27,21,0.5)] sm:p-10 lg:p-12"
    >
      <InvoiceDocumentHeader {...props} />
      <InvoiceDocumentParties {...props} />
      <InvoiceItemsSection {...props} />
      <InvoiceNotesTotalsSection {...props} />
    </section>
  );
}

function InvoiceDocumentHeader({
  invoiceNumber,
  senderName,
  senderSignatureUrl,
  form,
  readOnly,
  setForm,
  setCurrency,
}: {
  invoiceNumber: number;
  senderName: string;
  senderSignatureUrl: string | null;
  form: InvoiceFormState;
  readOnly: boolean;
  setForm: React.Dispatch<React.SetStateAction<InvoiceFormState>>;
  setCurrency: (currency: Currency) => void;
}): React.ReactElement {
  return (
    <section className="grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,26rem)] lg:items-start">
      <SenderIdentityBlock senderName={senderName} signaturePreviewUrl={senderSignatureUrl} />
      <div className="min-w-0 space-y-5 lg:text-right">
        <h2
          aria-label={`INVOICE #${invoiceNumber}`}
          className="break-words space-y-1 lg:flex lg:flex-col lg:items-end"
        >
          <span
            aria-hidden="true"
            className="block text-[11px] font-semibold uppercase tracking-[0.32em] text-ochre"
          >
            INVOICE
          </span>
          <span
            aria-hidden="true"
            className="block font-serif text-5xl italic leading-none tabular-nums text-ink sm:text-6xl"
          >
            #{invoiceNumber}
          </span>
        </h2>
        <div className="grid min-w-0 max-w-full grid-cols-1 gap-3 sm:grid-cols-3 lg:ml-auto lg:max-w-xs lg:grid-cols-1 lg:text-left">
          <DateField
            id="invoice-date"
            label="Date"
            value={form.date}
            disabled={readOnly}
            onChange={(event) => setFormField('date', event.currentTarget.value, setForm)}
          />
          <DateField
            id="invoice-due-date"
            label="Due date"
            value={form.dueDate}
            disabled={readOnly}
            onChange={(event) => setFormField('dueDate', event.currentTarget.value, setForm)}
          />
          <CurrencySelect value={form.currency} disabled={readOnly} onChange={setCurrency} />
        </div>
      </div>
    </section>
  );
}

function InvoiceDocumentParties({
  form,
  readOnly,
  selectedParties,
  updateParty,
}: {
  form: InvoiceFormState;
  readOnly: boolean;
  selectedParties: { billTo: CustomerPickerValue | null; shipTo: CustomerPickerValue | null };
  updateParty: (kind: 'billTo' | 'shipTo', customer: Counterparty | null) => void;
}): React.ReactElement {
  return (
    <section className="mt-8 grid min-w-0 gap-5 lg:grid-cols-2">
      <CustomerPicker
        id="bill-to-customer"
        label="Bill To"
        addressKind="billTo"
        value={selectedParties.billTo}
        preservedSnapshot={form.billTo.intent === 'keepSnapshot' ? form.billTo.snapshot : null}
        disabled={readOnly}
        onChange={(customer) => updateParty('billTo', customer)}
      />
      <CustomerPicker
        id="ship-to-customer"
        label="Ship To"
        addressKind="shipTo"
        value={selectedParties.shipTo}
        preservedSnapshot={form.shipTo.intent === 'keepSnapshot' ? form.shipTo.snapshot : null}
        disabled={readOnly}
        onChange={(customer) => updateParty('shipTo', customer)}
      />
    </section>
  );
}

function InvoiceItemsSection({
  form,
  readOnly,
  setForm,
}: InvoiceDocumentSurfaceProps): React.ReactElement {
  return (
    <div className="mt-8">
      <InvoiceItemsTable
        currency={form.currency}
        items={form.items}
        readOnly={readOnly}
        onChange={(items) => setForm((current) => ({ ...current, items }))}
      />
    </div>
  );
}

function InvoiceNotesTotalsSection({
  form,
  readOnly,
  setForm,
  setNotesPristine,
}: InvoiceDocumentSurfaceProps): React.ReactElement {
  return (
    <section className="mt-10 grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
      <TextArea
        id="invoice-notes"
        label="Notes"
        rows={6}
        value={form.notes}
        disabled={readOnly}
        onChange={(event) => {
          setNotesPristine(false);
          setFormField('notes', event.currentTarget.value, setForm);
        }}
      />
      <InvoiceTotals
        currency={form.currency}
        items={form.items}
        taxRate={form.taxRate}
        taxLabel={form.taxLabel}
        readOnly={readOnly}
        onTaxRateChange={(value) => setForm((current) => ({ ...current, taxRate: value }))}
        onTaxLabelChange={(value) => setForm((current) => ({ ...current, taxLabel: value }))}
      />
    </section>
  );
}

function InvoiceEditorHeader({ title }: { title: string }): React.ReactElement {
  return (
    <header className="flex min-w-0 items-center gap-3">
      <Link
        to="/"
        aria-label="Back"
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line bg-paper text-xl leading-none text-ink-soft transition hover:border-ochre/40 hover:bg-paper-deep"
      >
        ‹
      </Link>
      <h1 className="min-w-0 break-words font-serif text-2xl italic leading-tight text-ink sm:text-3xl">
        {title}
      </h1>
    </header>
  );
}

function InvoiceEditorStatus({
  archiveInvoice,
  paymentStatus,
  saveMessage,
  saveError,
  paymentError,
  deleteError,
  pdfIssue,
  className,
}: {
  archiveInvoice: boolean;
  paymentStatus: 'Paid' | 'Unpaid' | null;
  saveMessage: string | null;
  saveError: string | null;
  paymentError: string | null;
  deleteError: string | null;
  pdfIssue: string | null;
  className?: string;
}): React.ReactElement | null {
  const uniquePdfIssue = pdfIssue === saveError ? null : pdfIssue;
  if (
    !archiveInvoice &&
    !paymentStatus &&
    !saveMessage &&
    !saveError &&
    !paymentError &&
    !deleteError &&
    !uniquePdfIssue
  ) {
    return null;
  }

  return (
    <section aria-label="Invoice editor status" className={classNames('space-y-2', className)}>
      {archiveInvoice ? <StatusMessage tone="pending">Archive invoice</StatusMessage> : null}
      {paymentStatus ? <StatusMessage tone="pending">{paymentStatus}</StatusMessage> : null}
      {saveMessage ? <StatusMessage tone="success">{saveMessage}</StatusMessage> : null}
      {saveError ? <StatusMessage tone="error">{saveError}</StatusMessage> : null}
      {paymentError ? <StatusMessage tone="error">{paymentError}</StatusMessage> : null}
      {deleteError ? <StatusMessage tone="error">{deleteError}</StatusMessage> : null}
      {uniquePdfIssue ? <StatusMessage tone="pending">{uniquePdfIssue}</StatusMessage> : null}
    </section>
  );
}

function InvoiceEditorActions({
  label,
  className,
  readOnly,
  busy,
  savingMode,
  save,
  downloadInvoice,
  signature,
  signatureLoading,
  download,
  archiveInvoiceId,
  downloadOriginal,
  paymentToggle,
  deleteAction,
}: InvoiceEditorActionsProps): React.ReactElement {
  return (
    <section aria-label={label} className={classNames('space-y-3', className)}>
      <EditorSaveButton
        mode="stay"
        readOnly={readOnly}
        busy={busy}
        savingMode={savingMode}
        save={save}
      />
      <EditorSaveButton
        mode="close"
        readOnly={readOnly}
        busy={busy}
        savingMode={savingMode}
        save={save}
      />
      {paymentToggle ? <PaymentToggleButton {...paymentToggle} disabledByBusy={busy} /> : null}
      <DownloadPdfButton
        invoice={downloadInvoice}
        signature={signature}
        disabled={busy || signatureLoading || downloadInvoice === null}
        className="w-full"
        buttonClassName="w-full py-3"
        download={download}
      />
      {archiveInvoiceId ? (
        <DownloadOriginalButton invoiceId={archiveInvoiceId} download={downloadOriginal} />
      ) : null}
      {deleteAction ? <DeleteInvoiceButton {...deleteAction} disabledByBusy={busy} /> : null}
    </section>
  );
}

function DeleteInvoiceButton({
  invoiceNumber,
  pending,
  disabledByBusy,
  requestDelete,
}: DeleteActionState & { disabledByBusy: boolean }): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={`Delete invoice #${invoiceNumber}`}
      disabled={pending || disabledByBusy}
      onClick={requestDelete}
      className="inline-flex min-h-11 w-full items-center justify-center rounded-full border border-clay/40 bg-paper px-4 py-3 text-sm font-semibold text-clay transition hover:border-clay/70 hover:bg-clay/10 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {pending ? 'Deleting…' : 'Delete invoice'}
    </button>
  );
}

function PaymentToggleButton({
  isPaid,
  pending,
  disabledByBusy,
  toggle,
}: PaymentToggleState & { disabledByBusy: boolean }): React.ReactElement {
  const label = pending
    ? isPaid
      ? 'Marking unpaid…'
      : 'Marking paid…'
    : isPaid
      ? 'Mark unpaid'
      : 'Mark paid';
  const ariaLabel = isPaid ? 'Mark invoice as unpaid' : 'Mark invoice as paid';
  const toneClass = isPaid
    ? 'border-line bg-paper text-ink hover:border-ochre/40 hover:bg-paper-deep'
    : 'border-sage/45 bg-sage/10 text-sage hover:border-sage/70 hover:bg-sage/15';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={pending || disabledByBusy}
      onClick={toggle}
      className={`inline-flex min-h-11 w-full items-center justify-center rounded-full border px-4 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${toneClass}`}
    >
      {label}
    </button>
  );
}

function EditorSaveButton({
  mode,
  readOnly,
  busy,
  savingMode,
  save,
}: Pick<InvoiceEditorActionState, 'readOnly' | 'busy' | 'savingMode' | 'save'> & {
  mode: SaveMode;
}): React.ReactElement {
  const isClose = mode === 'close';
  return (
    <button
      type="button"
      disabled={readOnly || busy}
      onClick={() => {
        void save(mode);
      }}
      className={isClose ? secondaryActionButtonClassName : primaryActionButtonClassName}
    >
      {savingMode === mode ? 'Saving…' : isClose ? 'Save and close' : 'Save invoice'}
    </button>
  );
}

function SenderIdentityBlock({
  senderName,
  signaturePreviewUrl,
}: {
  senderName: string;
  signaturePreviewUrl: string | null;
}): React.ReactElement {
  return (
    <div className="min-w-0 max-w-sm space-y-4">
      <div className="space-y-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink-muted">From</p>
        <p className="break-words font-serif text-xl italic leading-tight text-ink">{senderName}</p>
      </div>
      {signaturePreviewUrl ? (
        <div className="flex min-h-20 items-center">
          <img
            alt=""
            aria-hidden="true"
            src={signaturePreviewUrl}
            className="max-h-20 max-w-full object-contain sm:max-w-64"
          />
        </div>
      ) : null}
    </div>
  );
}

function CurrencySelect({
  value,
  disabled,
  onChange,
}: {
  value: Currency;
  disabled?: boolean;
  onChange: (currency: Currency) => void;
}): React.ReactElement {
  return (
    <div className="min-w-0 space-y-1.5">
      <label
        className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-muted"
        htmlFor="invoice-currency"
      >
        Currency
      </label>
      <NativeSelect
        id="invoice-currency"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.value as Currency)}
        className="min-w-0 max-w-full disabled:text-ink-muted"
        wrapperClassName="min-w-0 max-w-full"
      >
        <option value="EUR">EUR</option>
        <option value="USD">USD</option>
        <option value="RUB">RUB</option>
      </NativeSelect>
    </div>
  );
}

function DateField({
  id,
  label,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
}): React.ReactElement {
  return (
    <TextField
      id={id}
      type="date"
      label={label}
      value={value}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

function toInitialForm(settings: SettingsResponse, invoice?: InvoiceDetail): InvoiceFormState {
  if (invoice) {
    return {
      date: invoice.date,
      dueDate: invoice.dueDate ?? '',
      currency: invoice.currency,
      billTo: partyFromInvoice(invoice, 'billTo'),
      shipTo: partyFromInvoice(invoice, 'shipTo'),
      notes: invoice.notes,
      items: invoice.items.map((item) => ({
        description: item.description,
        quantity: item.quantity,
        rate: item.rate,
      })),
      taxRate: invoice.taxRate,
      taxLabel: invoice.taxLabel ?? '',
    };
  }

  return {
    date: todayLocalIsoDate(),
    dueDate: '',
    currency: 'EUR',
    billTo: { intent: 'none', snapshot: null },
    shipTo: { intent: 'none', snapshot: null },
    notes: settings.settings.notesTemplateEur,
    items: [{ description: 'Services', quantity: '1', rate: '1' }],
    taxRate: '0',
    taxLabel: '',
  };
}

function partyFromInvoice(invoice: InvoiceDetail, kind: 'billTo' | 'shipTo'): PartyState {
  const id = kind === 'billTo' ? invoice.billToId : invoice.shipToId;
  const snapshot = kind === 'billTo' ? invoice.billToSnapshot : invoice.shipToSnapshot;
  if (id) return { intent: 'selectCustomer', customerId: id, snapshot };
  if (snapshot) return { intent: 'keepSnapshot', snapshot };
  return { intent: 'none', snapshot: null };
}

function toUpsertInput(form: InvoiceFormState): UpsertInvoiceInput {
  const trimmedRate = form.taxRate.trim();
  const normalizedTaxRate = trimmedRate === '' ? '0' : trimmedRate;
  const trimmedLabel = form.taxLabel.trim();
  return {
    date: form.date,
    dueDate: form.dueDate.trim() === '' ? null : form.dueDate,
    currency: form.currency,
    billTo: toPartyInput(form.billTo),
    shipTo: toPartyInput(form.shipTo),
    notes: form.notes,
    items: form.items.map((item) => ({
      description: item.description,
      quantity: item.quantity,
      rate: item.rate,
    })),
    taxRate: normalizedTaxRate,
    taxLabel: trimmedLabel === '' ? null : trimmedLabel,
  };
}

function toPartyInput(party: PartyState): InvoicePartyInput {
  if (party.intent === 'selectCustomer' && party.customerId) {
    return { type: 'selectCustomer', customerId: party.customerId };
  }
  if (party.intent === 'keepSnapshot') return { type: 'keepSnapshot' };
  if (party.intent === 'clear') return { type: 'clear' };
  return { type: 'none' };
}

function buildEditorPdfCandidate({
  form,
  invoice,
  invoiceNumber,
  partySnapshots,
  settings,
}: {
  form: InvoiceFormState;
  invoice?: InvoiceDetail;
  invoiceNumber: number;
  partySnapshots: { billTo: InvoiceSnapshot | null; shipTo: InvoiceSnapshot | null };
  settings: SettingsResponse;
}): EditorPdfCandidate {
  if (invoice?.isArchive) {
    const model = invoiceDetailToPdfModel(invoice);
    const preflight = validatePdfOnePageLayout(model);
    if (!preflight.ok) return { kind: 'overflow', message: preflight.error };
    return { kind: 'ready', model };
  }

  const draftInput: PdfInvoiceDraftInput = {
    number: invoiceNumber,
    date: form.date,
    dueDate: form.dueDate.trim() === '' ? null : form.dueDate,
    currency: form.currency,
    sender: {
      id: invoice?.senderProfile.id ?? settings.senderProfile.id,
      name: invoice?.senderProfile.name ?? settings.senderProfile.name,
      signaturePreviewUrl: invoice
        ? invoice.senderProfile.signaturePreviewUrl
        : settings.senderProfile.signaturePreviewUrl,
      bankRequisites: bankRequisitesFor(settings, form.currency),
    },
    billTo: toPdfParty(partySnapshots.billTo),
    shipTo: toPdfParty(partySnapshots.shipTo),
    notes: form.notes,
    items: form.items.map((item) => ({
      description: item.description,
      quantity: item.quantity,
      rate: item.rate,
    })),
    taxRate: form.taxRate.trim() === '' ? '0' : form.taxRate.trim(),
    taxLabel: form.taxLabel.trim() === '' ? null : form.taxLabel.trim(),
  };

  const result = invoiceDraftToPdfModel(draftInput);
  if (!result.ok) return { kind: 'invalid', message: result.error };

  const model = invoice ? { ...result.model, id: invoice.id } : result.model;
  const preflight = validatePdfOnePageLayout(model);
  if (!preflight.ok) return { kind: 'overflow', message: preflight.error };

  return { kind: 'ready', model };
}

function DownloadOriginalButton({
  invoiceId,
  download = downloadOriginalInvoicePdf,
}: {
  invoiceId: string;
  download?: (invoiceId: string) => Promise<void>;
}): React.ReactElement {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDownload(): Promise<void> {
    if (pending) return;
    setError(null);
    setPending(true);
    try {
      await download(invoiceId);
    } catch {
      setError(ORIGINAL_PDF_DOWNLOAD_ERROR);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        disabled={pending}
        onClick={() => {
          void handleDownload();
        }}
        className="inline-flex w-full items-center justify-center rounded-full border border-line bg-paper px-4 py-3 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep disabled:cursor-not-allowed disabled:opacity-60"
      >
        {pending ? 'Downloading...' : 'Download original'}
      </button>
      {error ? (
        <StatusMessage tone="error" className="max-w-sm">
          {error}
        </StatusMessage>
      ) : null}
    </div>
  );
}

function toPdfParty(snapshot: InvoiceSnapshot | null): PdfParty | null {
  if (!snapshot) return null;
  return {
    name: snapshot.name,
    email: snapshot.email,
    billTo: snapshot.billTo,
    shipTo: snapshot.shipTo,
  };
}

function usePdfSignature(
  signaturePreviewUrl: string | null,
  signatureLoader = loadPdfSignature,
): { signature: PdfSignature | null; loading: boolean } {
  const [signatureState, setSignatureState] = useState<{
    url: string;
    signature: PdfSignature | null;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!signaturePreviewUrl) return () => undefined;

    void signatureLoader(signaturePreviewUrl)
      .then((loadedSignature) => {
        if (!cancelled) {
          setSignatureState({ url: signaturePreviewUrl, signature: loadedSignature ?? null });
        }
      })
      .catch(() => {
        if (!cancelled) setSignatureState({ url: signaturePreviewUrl, signature: null });
      });

    return () => {
      cancelled = true;
    };
  }, [signatureLoader, signaturePreviewUrl]);

  if (!signaturePreviewUrl) return { signature: null, loading: false };
  if (signatureState?.url !== signaturePreviewUrl) return { signature: null, loading: true };
  return { signature: signatureState.signature, loading: false };
}

function resolvePartySnapshot(
  party: PartyState,
  customers: Counterparty[],
): InvoiceSnapshot | null {
  if (party.intent === 'keepSnapshot') return party.snapshot ?? null;
  if (party.intent !== 'selectCustomer' || !party.customerId) return null;
  const customer = customers.find((candidate) => candidate.id === party.customerId);
  if (customer) return snapshotFromCustomer(customer);
  return party.snapshot ?? null;
}

function resolveSelectedCustomer(
  party: PartyState,
  customers: Counterparty[],
): CustomerPickerValue | null {
  if (party.intent !== 'selectCustomer' || !party.customerId) return null;
  const customer = customers.find((candidate) => candidate.id === party.customerId);
  if (customer) return customer;
  if (!party.snapshot) return null;
  return {
    id: party.customerId,
    name: party.snapshot.name,
    email: party.snapshot.email,
    billTo: party.snapshot.billTo,
    shipTo: party.snapshot.shipTo,
  };
}

function snapshotFromCustomer(customer: Counterparty | CustomerPickerValue): InvoiceSnapshot {
  return {
    name: customer.name,
    email: customer.email,
    billTo: customer.billTo,
    shipTo: customer.shipTo,
  };
}

function notesTemplateFor(settings: SettingsResponse, currency: Currency): string {
  if (currency === 'USD') return settings.settings.notesTemplateUsd;
  if (currency === 'RUB') return settings.settings.notesTemplateRub;
  return settings.settings.notesTemplateEur;
}

function bankRequisitesFor(settings: SettingsResponse, currency: Currency): string {
  if (currency === 'USD') return settings.senderProfile.bankRequisitesUsd;
  if (currency === 'RUB') return settings.senderProfile.bankRequisitesRub;
  return settings.senderProfile.bankRequisitesEur;
}

function setFormField(
  field: 'date' | 'dueDate' | 'notes',
  value: string,
  setForm: React.Dispatch<React.SetStateAction<InvoiceFormState>>,
): void {
  setForm((current) => ({ ...current, [field]: value }));
}

const primaryActionButtonClassName =
  'inline-flex min-h-11 w-full items-center justify-center rounded-full bg-ochre px-4 py-3 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(176,119,43,0.85)] transition hover:bg-ochre-deep disabled:cursor-not-allowed disabled:opacity-60';

const secondaryActionButtonClassName =
  'inline-flex min-h-11 w-full items-center justify-center rounded-full border border-line bg-paper px-4 py-3 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep disabled:cursor-not-allowed disabled:opacity-60';

function classNames(...values: Array<string | undefined>): string {
  return values.filter(Boolean).join(' ');
}
